'use client'

import { Cloud, Code2, Cpu, Database, Globe, Layout, Server, Terminal } from 'lucide-react'
import React from 'react'

// --- SHARED TYPES ---
interface StackProps {
  stack: string[]
  gradient: string
  compact?: boolean
  className?: string
  transparent?: boolean
}

// --- VARIANT 1: ORBITAL NETWORK ---
// Technologies orbiting a central core
export function StackOrbital({ stack, gradient, compact, className, transparent }: StackProps) {
  // Split stack into rings for distribution
  const ring1 = stack.slice(0, 4)
  const ring2 = stack.slice(4, 9)
  const ring3 = stack.slice(9)

  // Helper to get category icon
  const getTechIcon = (tech: string) => {
    const t = tech.toLowerCase()
    if (t.includes('react') || t.includes('next') || t.includes('vue') || t.includes('angular'))
      return Globe
    if (t.includes('node') || t.includes('php') || t.includes('python') || t.includes('go'))
      return Server
    if (t.includes('sql') || t.includes('data') || t.includes('prisma') || t.includes('mongo'))
      return Database
    if (t.includes('design') || t.includes('figma') || t.includes('adobe') || t.includes('ui'))
      return Layout
    if (t.includes('docker') || t.includes('aws') || t.includes('cloud') || t.includes('deploy'))
      return Cloud
    return Code2
  }

  return (
    <section
      className={[
        compact ? 'py-0' : 'py-12 sm:py-16 lg:py-20',
        'group/orbitals relative overflow-hidden',
        className || '',
      ]
        .join(' ')
        .trim()}
    >
      {/* Orbital network (kept on mobile; scaled to fit) */}
      <div className="relative -my-6 flex h-[520px] scale-[0.52] items-center justify-center sm:-my-10 sm:h-[640px] sm:scale-[0.62] md:-my-20 md:h-[800px] md:scale-[0.75] lg:scale-90 xl:scale-100">
        {/* Central Core */}
        <div
          className={[
            'relative z-10 flex h-28 w-28 items-center justify-center rounded-full border border-white/20 shadow-[0_0_80px_rgba(59,130,246,0.5)] sm:h-36 sm:w-36 md:h-40 md:w-40',
            // Optional "no background card" look (used on directions page).
            // Keep the glow and border for legibility.
            transparent ? 'bg-transparent' : 'bg-[#0c1220]',
          ].join(' ')}
        >
          <div
            className={`absolute inset-0 rounded-full bg-gradient-to-br ${gradient} animate-pulse opacity-30 blur-2xl`}
          />
          <div className="relative z-10 flex flex-col items-center">
            <Cpu className="mb-2 h-10 w-10 text-white sm:h-14 sm:w-14 md:h-16 md:w-16" />
            <span className="font-mono text-[10px] tracking-widest text-blue-200 uppercase sm:text-xs">
              Core System
            </span>
          </div>
        </div>

        {/* Orbital Rings - CSS animations */}
        <style jsx>{`
          @keyframes orbit-cw {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes orbit-ccw {
            from {
              transform: rotate(360deg);
            }
            to {
              transform: rotate(0deg);
            }
          }

          .orbit-container-cw {
            position: absolute;
            width: 100%;
            height: 100%;
            animation: orbit-cw linear infinite;
          }
          .orbit-container-ccw {
            position: absolute;
            width: 100%;
            height: 100%;
            animation: orbit-ccw linear infinite;
          }

          /* Placer positions the item on the ring statically relative to container */
          .orbit-placer {
            position: absolute;
            top: 50%;
            left: 50%;
          }

          /* Content rotates opposite to container to stay upright */
          .orbit-content-ccw {
            animation: orbit-ccw linear infinite;
          }
          .orbit-content-cw {
            animation: orbit-cw linear infinite;
          }

          /* Pause on hover for ALL animations */
          .group\/orbitals:hover .orbit-container-cw,
          .group\/orbitals:hover .orbit-container-ccw,
          .group\/orbitals:hover .orbit-content-cw,
          .group\/orbitals:hover .orbit-content-ccw {
            animation-play-state: paused;
          }
        `}</style>

        {/* Ring 1 (Inner) - CW */}
        <div className="absolute h-[380px] w-[380px] rounded-full border border-white/5">
          <div className="orbit-container-cw" style={{ animationDuration: '30s' }}>
            {ring1.map((tech, i) => {
              const angle = (i / ring1.length) * 360
              const Icon = getTechIcon(tech)
              return (
                <div
                  key={i}
                  className="orbit-placer"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${angle}deg) translate(190px) rotate(-${angle}deg)`,
                  }}
                >
                  <div className="orbit-content-ccw" style={{ animationDuration: '30s' }}>
                    <div
                      className={[
                        'group/item z-20 flex cursor-default items-center gap-3 rounded-2xl border border-white/10 px-5 py-3 shadow-lg transition-all hover:scale-110 hover:border-blue-400/50 hover:shadow-blue-500/20',
                        transparent ? 'bg-transparent' : 'bg-[#0c1220]',
                      ].join(' ')}
                    >
                      <div
                        className={`rounded-lg bg-white/5 p-2 group-hover/item:bg-gradient-to-br ${gradient}`}
                      >
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-lg font-bold text-gray-200 transition-colors group-hover/item:text-white">
                        {tech}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Ring 2 (Middle) - CCW (Reverse) */}
        <div className="absolute h-[600px] w-[600px] rounded-full border border-dashed border-white/10 opacity-60">
          <div className="orbit-container-ccw" style={{ animationDuration: '45s' }}>
            {ring2.map((tech, i) => {
              const angle = (i / ring2.length) * 360
              const Icon = getTechIcon(tech)
              return (
                <div
                  key={i}
                  className="orbit-placer"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${angle}deg) translate(300px) rotate(-${angle}deg)`,
                  }}
                >
                  <div className="orbit-content-cw" style={{ animationDuration: '45s' }}>
                    <div
                      className={[
                        'group/item z-10 flex cursor-default items-center gap-3 rounded-xl border border-white/10 px-4 py-2.5 transition-all hover:border-white/30',
                        transparent
                          ? 'bg-transparent'
                          : 'bg-[#111827]/90 backdrop-blur-md hover:bg-[#1f2937]',
                      ].join(' ')}
                    >
                      <Icon className="h-4 w-4 text-gray-400 transition-colors group-hover/item:text-blue-400" />
                      <span className="text-base font-medium text-gray-300 transition-colors group-hover/item:text-white">
                        {tech}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Ring 3 (Outer) - CW */}
        <div className="absolute h-[820px] w-[820px] rounded-full border border-white/5 opacity-40">
          <div className="orbit-container-cw" style={{ animationDuration: '70s' }}>
            {ring3.map((tech, i) => {
              const angle = (i / ring3.length) * 360
              return (
                <div
                  key={i}
                  className="orbit-placer"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${angle}deg) translate(410px) rotate(-${angle}deg)`,
                  }}
                >
                  <div className="orbit-content-ccw" style={{ animationDuration: '70s' }}>
                    <div
                      className={[
                        'cursor-default rounded-lg border border-white/5 px-4 py-2 text-sm text-gray-400 transition-all hover:border-white/20 hover:text-white',
                        transparent ? 'bg-transparent' : 'bg-white/[0.02]',
                      ].join(' ')}
                    >
                      {tech}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

// --- VARIANT 2: CODE TERMINAL ---
// IDE Aesthetic with syntax highlighting
export function StackTerminal({ stack, gradient }: StackProps) {
  return (
    <section className="py-20">
      {/* Header */}
      <div className="mb-12 flex items-center gap-4 opacity-50">
        <span className="font-mono text-sm tracking-widest text-white/50 uppercase">
          [ VARIANT 02 : CODE TERMINAL ]
        </span>
        <div className={`h-[1px] flex-1 bg-gradient-to-r ${gradient} opacity-40`} />
      </div>

      <div className="mx-auto max-w-4xl">
        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#1e1e1e] font-mono text-sm shadow-2xl md:text-base">
          {/* Window Controls */}
          <div className="flex items-center gap-2 border-b border-white/5 bg-[#2d2d2d] px-4 py-3">
            <div className="h-3 w-3 rounded-full bg-red-500/80" />
            <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
            <div className="h-3 w-3 rounded-full bg-green-500/80" />
            <div className="ml-4 flex items-center gap-2 text-xs text-gray-500">
              <Terminal className="h-3 w-3" />
              stack.config.json
            </div>
          </div>

          {/* IDE Content */}
          <div className="overflow-x-auto p-6 leading-relaxed text-gray-300 md:p-10">
            <div className="flex">
              <span className="mr-4 w-6 text-right text-gray-600 select-none">1</span>
              <div>
                <span className="text-purple-400">export const</span>{' '}
                <span className="text-blue-400">DreamStack</span> ={' '}
                <span className="text-yellow-400">{'{'}</span>
              </div>
            </div>

            <div className="flex">
              <span className="mr-4 w-6 text-right text-gray-600 select-none">2</span>
              <div className="pl-8">
                <span className="text-sky-300">&quot;core&quot;</span>:{' '}
                <span className="text-green-400">
                  [&apos;Next.js&apos;, &apos;React&apos;, &apos;TypeScript&apos;]
                </span>
                ,
              </div>
            </div>

            <div className="flex">
              <span className="mr-4 w-6 text-right text-gray-600 select-none">3</span>
              <div className="pl-8">
                <span className="text-sky-300">&quot;styling&quot;</span>:{' '}
                <span className="text-green-400">
                  [&apos;Tailwind CSS&apos;, &apos;GSAP&apos;, &apos;Three.js&apos;]
                </span>
                ,
              </div>
            </div>

            <div className="flex">
              <span className="mr-4 w-6 text-right text-gray-600 select-none">4</span>
              <div className="pl-8">
                <span className="text-sky-300">&quot;backend&quot;</span>:{' '}
                <span className="text-yellow-400">{'['}</span>
              </div>
            </div>

            {/* Dynamic List */}
            {stack
              .filter(
                (t) =>
                  !['Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'GSAP', 'Three.js'].includes(
                    t,
                  ),
              )
              .map((tech, i) => (
                <div key={i} className="group flex transition-colors hover:bg-white/5">
                  <span className="mr-4 w-6 text-right text-gray-600 select-none">{i + 5}</span>
                  <div className="pl-12">
                    <span className="text-orange-400">&quot;{tech}&quot;</span>,{' '}
                    <span className="ml-4 text-xs text-gray-500 opacity-0 transition-opacity group-hover:opacity-100">
                      {'/* '}
                      {['High Performance', 'Scalable', 'Secure', 'Optimized'][i % 4]}
                      {' */'}
                    </span>
                  </div>
                </div>
              ))}

            <div className="flex">
              <span className="mr-4 w-6 text-right text-gray-600 select-none">
                {stack.length + 5}
              </span>
              <div className="pl-8">
                <span className="text-yellow-400">{']'}</span>
              </div>
            </div>

            <div className="flex">
              <span className="mr-4 w-6 text-right text-gray-600 select-none">
                {stack.length + 6}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-yellow-400">{'}'}</span>
                <span className="ml-1 h-5 w-2 animate-pulse bg-blue-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
